import type {
  CreateHolidayInput,
  CreateHolidaysBatchInput,
  CreateHolidaysBatchResult,
  Holiday,
  HolidayListQuery,
  UpdateHolidayInput,
} from "dutyon-server/types";
import { httpClient } from "./httpClient";

export type GetHolidaysParams = HolidayListQuery;

export interface GetHolidaysResponse {
  list: Holiday[];
  total: number;
}

export const getHolidays = async (params?: GetHolidaysParams): Promise<GetHolidaysResponse> => {
  const response = await httpClient.get<GetHolidaysResponse>("/holidays", { params });
  return response.data;
};

export const createHoliday = async (data: CreateHolidayInput): Promise<Holiday> => {
  const response = await httpClient.post<Holiday>("/holidays", data);
  return response.data;
};

export const createHolidaysBatch = async (
  data: CreateHolidaysBatchInput,
): Promise<CreateHolidaysBatchResult> => {
  const response = await httpClient.post<CreateHolidaysBatchResult>("/holidays/batch", data);
  return response.data;
};

export const updateHoliday = async ({
  id,
  data,
}: {
  id: string;
  data: UpdateHolidayInput;
}): Promise<Holiday> => {
  const response = await httpClient.put<Holiday>(`/holidays/${id}`, data);
  return response.data;
};

export const deleteHoliday = async ({ id }: { id: string }): Promise<Holiday> => {
  const response = await httpClient.delete<Holiday>(`/holidays/${id}`);
  return response.data;
};

export interface KoreanHolidayPreset {
  date: string; // YYYY-MM-DD
  name: string;
  isSubstitute?: boolean;
}

const KOREAN_HOLIDAY_DATA: Record<number, KoreanHolidayPreset[]> = {
  2024: [
    { date: "2024-01-01", name: "신정" },
    { date: "2024-02-09", name: "설날 연휴" },
    { date: "2024-02-10", name: "설날" },
    { date: "2024-02-11", name: "설날 연휴" },
    { date: "2024-02-12", name: "대체공휴일 (설날)", isSubstitute: true },
    { date: "2024-03-01", name: "3·1절" },
    { date: "2024-04-10", name: "제22대 국회의원 선거" },
    { date: "2024-05-05", name: "어린이날" },
    { date: "2024-05-06", name: "대체공휴일 (어린이날)", isSubstitute: true },
    { date: "2024-05-15", name: "부처님오신날" },
    { date: "2024-06-06", name: "현충일" },
    { date: "2024-08-15", name: "광복절" },
    { date: "2024-09-16", name: "추석 연휴" },
    { date: "2024-09-17", name: "추석" },
    { date: "2024-09-18", name: "추석 연휴" },
    { date: "2024-10-01", name: "국군의 날 (임시공휴일)", isSubstitute: true },
    { date: "2024-10-03", name: "개천절" },
    { date: "2024-10-09", name: "한글날" },
    { date: "2024-12-25", name: "크리스마스" },
  ],
  2025: [
    { date: "2025-01-01", name: "신정" },
    { date: "2025-01-28", name: "설날 연휴" },
    { date: "2025-01-29", name: "설날" },
    { date: "2025-01-30", name: "설날 연휴" },
    { date: "2025-03-01", name: "3·1절" },
    { date: "2025-03-03", name: "대체공휴일 (3·1절)", isSubstitute: true },
    { date: "2025-05-05", name: "어린이날 / 부처님오신날" },
    { date: "2025-05-06", name: "대체공휴일 (어린이날)", isSubstitute: true },
    { date: "2025-06-06", name: "현충일" },
    { date: "2025-08-15", name: "광복절" },
    { date: "2025-10-03", name: "개천절" },
    { date: "2025-10-05", name: "추석 연휴" },
    { date: "2025-10-06", name: "추석" },
    { date: "2025-10-07", name: "추석 연휴" },
    { date: "2025-10-08", name: "대체공휴일 (추석)", isSubstitute: true },
    { date: "2025-10-09", name: "한글날" },
    { date: "2025-12-25", name: "크리스마스" },
  ],
  2026: [
    { date: "2026-01-01", name: "신정" },
    { date: "2026-02-16", name: "설날 연휴" },
    { date: "2026-02-17", name: "설날" },
    { date: "2026-02-18", name: "설날 연휴" },
    { date: "2026-03-01", name: "3·1절" },
    { date: "2026-03-02", name: "대체공휴일 (3·1절)", isSubstitute: true },
    { date: "2026-05-05", name: "어린이날" },
    { date: "2026-05-24", name: "부처님오신날" },
    { date: "2026-05-25", name: "대체공휴일 (부처님오신날)", isSubstitute: true },
    { date: "2026-06-03", name: "제9회 전국동시지방선거" },
    { date: "2026-06-06", name: "현충일" },
    { date: "2026-08-15", name: "광복절" },
    { date: "2026-08-17", name: "대체공휴일 (광복절)", isSubstitute: true },
    { date: "2026-09-24", name: "추석 연휴" },
    { date: "2026-09-25", name: "추석" },
    { date: "2026-09-26", name: "추석 연휴" },
    { date: "2026-09-28", name: "대체공휴일 (추석)", isSubstitute: true },
    { date: "2026-10-03", name: "개천절" },
    { date: "2026-10-05", name: "대체공휴일 (개천절)", isSubstitute: true },
    { date: "2026-10-09", name: "한글날" },
    { date: "2026-12-25", name: "크리스마스" },
  ],
  2027: [
    { date: "2027-01-01", name: "신정" },
    { date: "2027-02-06", name: "설날 연휴" },
    { date: "2027-02-07", name: "설날" },
    { date: "2027-02-08", name: "설날 연휴" },
    { date: "2027-02-09", name: "대체공휴일 (설날)", isSubstitute: true },
    { date: "2027-03-01", name: "3·1절" },
    { date: "2027-03-03", name: "제21대 대통령 선거" },
    { date: "2027-05-05", name: "어린이날" },
    { date: "2027-05-13", name: "부처님오신날" },
    { date: "2027-06-06", name: "현충일" },
    { date: "2027-08-15", name: "광복절" },
    { date: "2027-08-16", name: "대체공휴일 (광복절)", isSubstitute: true },
    { date: "2027-09-14", name: "추석 연휴" },
    { date: "2027-09-15", name: "추석" },
    { date: "2027-09-16", name: "추석 연휴" },
    { date: "2027-10-03", name: "개천절" },
    { date: "2027-10-04", name: "대체공휴일 (개천절)", isSubstitute: true },
    { date: "2027-10-09", name: "한글날" },
    { date: "2027-10-11", name: "대체공휴일 (한글날)", isSubstitute: true },
    { date: "2027-12-25", name: "크리스마스" },
    { date: "2027-12-27", name: "대체공휴일 (크리스마스)", isSubstitute: true },
  ],
  2028: [
    { date: "2028-01-01", name: "신정" },
    { date: "2028-01-26", name: "설날 연휴" },
    { date: "2028-01-27", name: "설날" },
    { date: "2028-01-28", name: "설날 연휴" },
    { date: "2028-03-01", name: "3·1절" },
    { date: "2028-04-12", name: "제23대 국회의원 선거" },
    { date: "2028-05-02", name: "부처님오신날" },
    { date: "2028-05-05", name: "어린이날" },
    { date: "2028-06-06", name: "현충일" },
    { date: "2028-08-15", name: "광복절" },
    { date: "2028-10-02", name: "추석 연휴" },
    { date: "2028-10-03", name: "추석 / 개천절" },
    { date: "2028-10-04", name: "추석 연휴" },
    { date: "2028-10-05", name: "대체공휴일 (추석/개천절)", isSubstitute: true },
    { date: "2028-10-09", name: "한글날" },
    { date: "2028-12-25", name: "크리스마스" },
  ],
  2029: [
    { date: "2029-01-01", name: "신정" },
    { date: "2029-02-12", name: "설날 연휴" },
    { date: "2029-02-13", name: "설날" },
    { date: "2029-02-14", name: "설날 연휴" },
    { date: "2029-02-15", name: "대체공휴일 (설날)", isSubstitute: true },
    { date: "2029-03-01", name: "3·1절" },
    { date: "2029-05-05", name: "어린이날" },
    { date: "2029-05-07", name: "대체공휴일 (어린이날)", isSubstitute: true },
    { date: "2029-05-20", name: "부처님오신날" },
    { date: "2029-05-21", name: "대체공휴일 (부처님오신날)", isSubstitute: true },
    { date: "2029-06-06", name: "현충일" },
    { date: "2029-08-15", name: "광복절" },
    { date: "2029-09-21", name: "추석 연휴" },
    { date: "2029-09-22", name: "추석" },
    { date: "2029-09-23", name: "추석 연휴" },
    { date: "2029-09-24", name: "대체공휴일 (추석)", isSubstitute: true },
    { date: "2029-10-03", name: "개천절" },
    { date: "2029-10-09", name: "한글날" },
    { date: "2029-12-25", name: "크리스마스" },
  ],
  2030: [
    { date: "2030-01-01", name: "신정" },
    { date: "2030-02-02", name: "설날 연휴" },
    { date: "2030-02-03", name: "설날" },
    { date: "2030-02-04", name: "설날 연휴" },
    { date: "2030-02-05", name: "대체공휴일 (설날)", isSubstitute: true },
    { date: "2030-03-01", name: "3·1절" },
    { date: "2030-05-05", name: "어린이날" },
    { date: "2030-05-06", name: "대체공휴일 (어린이날)", isSubstitute: true },
    { date: "2030-05-09", name: "부처님오신날" },
    { date: "2030-06-06", name: "현충일" },
    { date: "2030-08-15", name: "광복절" },
    { date: "2030-09-11", name: "추석 연휴" },
    { date: "2030-09-12", name: "추석" },
    { date: "2030-09-13", name: "추석 연휴" },
    { date: "2030-10-03", name: "개천절" },
    { date: "2030-10-09", name: "한글날" },
    { date: "2030-12-25", name: "크리스마스" },
  ],
};

/**
 * 지정된 연도의 대한민국 공휴일 목록을 반환합니다.
 * (기본 지원 범위: 2024년 ~ 2030년)
 */
export const getKoreanHolidayPresets = (year: number): KoreanHolidayPreset[] => {
  if (KOREAN_HOLIDAY_DATA[year]) {
    return [...KOREAN_HOLIDAY_DATA[year]];
  }
  // 기본 고정 양력 공휴일 폴백
  return [
    { date: `${year}-01-01`, name: "신정" },
    { date: `${year}-03-01`, name: "3·1절" },
    { date: `${year}-05-05`, name: "어린이날" },
    { date: `${year}-06-06`, name: "현충일" },
    { date: `${year}-08-15`, name: "광복절" },
    { date: `${year}-10-03`, name: "개천절" },
    { date: `${year}-10-09`, name: "한글날" },
    { date: `${year}-12-25`, name: "크리스마스" },
  ];
};

